import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import CloseCircleIcon from '../CloseCircleIcon/CloseCircleIcon';

interface ResponsiveDialogueProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode; // made required
	width?: string;
  showCloseButton?: boolean;
  children: React.ReactNode;
  /** Optional test hook forwarded to the underlying MUI Dialog. */
  'data-testid'?: string;
  /** Suppresses MUI's default close-on-Escape behavior. */
  disableEscapeKeyDown?: boolean;
  /**
   * ARIA label id for the dialog. Forwarded to the underlying Dialog so
   * screen readers announce a title when `title` is rendered elsewhere
   * (e.g. inside the dialog body rather than in DialogTitle).
   */
  'aria-labelledby'?: string;
}

const ResponsiveDialogue: React.FC<ResponsiveDialogueProps> = ({
  open,
  onClose,
  title,
  width,
  showCloseButton = true,
  children,
  disableEscapeKeyDown = false,
  ...rest
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Route MUI's close signal through onClose only for close-button / programmatic
  // dismissal — never for backdrop click. This matches the design contract used
  // by modals like KeepAliveModal that require an explicit user choice.
  const handleClose = (_event: object, reason?: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick') {
      return;
    }
    if (reason === 'escapeKeyDown' && disableEscapeKeyDown) {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      data-testid={rest['data-testid'] ?? 'responsive-dialogue'}
      aria-labelledby={rest['aria-labelledby']}
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={disableEscapeKeyDown}
      fullWidth
      maxWidth={isMobile ? false : 'md'}
      fullScreen={isMobile}
      slotProps={{
        paper: {
          sx: isMobile
            ? {
                width: '100vw',
                maxWidth: '100vw',
                maxHeight: '100dvh',
                borderRadius: 0,
                m: 0,
                overflowY: 'auto',
              }
            : {
                maxHeight: '80vh',
                borderRadius: 2,
                m: 2,
                overflowY: 'auto',
								width: width || 'auto',
              },
        },
      }}
    >
      <DialogTitle sx={{ backgroundColor: 'grey.200', fontSize: isMobile ? '1.1rem' : '1.25rem', p: isMobile ? 1 : 2 }}>
        {title}
        {showCloseButton && (
          <IconButton
            aria-label="close"
            onClick={() => onClose()}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseCircleIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        {children}
      </DialogContent>
    </Dialog>
  );
};

export default ResponsiveDialogue;
